'use client'

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import SuggestionDropdown, {
    handleSuggestionKeyDown,
    type SuggestionItem
} from '@tebuto/forms/address-autocomplete/SuggestionDropdown'
import {
    combineStreetAndNumber,
    splitStreetAndNumber
} from '@tebuto/forms/address-autocomplete/split-street-and-number'
import {
    GERMAN_ZIP_REGEX,
    type StreetSuggestion,
    useStreetSuggestions
} from '@tebuto/forms/address-autocomplete/use-address-lookup'
import { cn } from '@tebuto/lib/utils'
import { useField, useFormikContext } from 'formik'
import { type ChangeEvent, type JSX, useEffect, useMemo, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import { getNestedValue } from './CityFormField'
import Description from './Description'
import ErrorMessage from './ErrorMessage'
import FormField, { type IFormField } from './Field'
import { Label } from './Label'
import { useFieldErrorVisible } from './useFieldErrorVisible'

type StreetFormFieldProps = Omit<IFormField, 'formatter' | 'type'> & {
    /** Formik field name of the sibling zip field — filters suggestions and gets autofilled on select */
    zipFieldName?: string
    /** Formik field name of the sibling city field — filters suggestions and gets autofilled on select */
    cityFieldName?: string
    /** Public API base URL override for the address lookup (used by the embedded widget) */
    lookupBaseUrl?: string
}

/**
 * Combined "street and number" input with street name typeahead. The typed value
 * is split into street-name and house-number tokens (every token containing a digit
 * counts as house number); only the street-name part is queried and the house number
 * is preserved through selection and appended to each suggestion label. When zip/city
 * are still empty, selecting a suggestion fills them as well.
 */
export default function StreetFormField({
    className = '',
    fieldClassName,
    labelClassName,
    label,
    name,
    autoComplete = 'street-address',
    labelTooltip,
    inputTooltip,
    description,
    placeholder,
    mandatory,
    readOnly,
    showErrors = true,
    height = 'default',
    zipFieldName,
    cityFieldName,
    lookupBaseUrl,
    'data-testid': testId
}: StreetFormFieldProps): JSX.Element {
    const showFieldError = useFieldErrorVisible(name)
    const [field, , helpers] = useField<string>(name)
    const { values, setFieldValue } = useFormikContext<unknown>()
    const isWidgetField = fieldClassName?.includes('widget-text-primary') ?? false
    const inputTooltipId = inputTooltip ? `${name}-input` : undefined

    const siblingZip = zipFieldName ? getNestedValue(values, zipFieldName) : undefined
    const siblingCity = cityFieldName ? getNestedValue(values, cityFieldName) : undefined
    const zipFilter = typeof siblingZip === 'string' && GERMAN_ZIP_REGEX.test(siblingZip) ? siblingZip : undefined
    const cityFilter = typeof siblingCity === 'string' && siblingCity.trim() ? siblingCity.trim() : undefined

    // Only queries the user actually typed trigger suggestions — never initial form values
    const [typedValue, setTypedValue] = useState('')
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)

    const { streetName, houseNumber } = useMemo(() => splitStreetAndNumber(typedValue), [typedValue])
    const { suggestions } = useStreetSuggestions(
        streetName,
        { zip: zipFilter, city: cityFilter },
        { baseUrl: lookupBaseUrl }
    )

    const items: SuggestionItem[] = useMemo(() => {
        const seen = new Set<string>()
        const deduplicated: SuggestionItem[] = []
        for (const street of suggestions) {
            const fillsLocality = !zipFilter
            const id = fillsLocality ? `${street.name}-${street.postalCode}-${street.locality}` : street.name
            if (seen.has(id)) {
                continue
            }
            seen.add(id)
            deduplicated.push({
                id,
                label: combineStreetAndNumber(street.name, houseNumber),
                detail: fillsLocality ? `${street.postalCode} ${street.locality}` : undefined
            })
        }
        return deduplicated
    }, [suggestions, houseNumber, zipFilter])

    useEffect(() => {
        if (!typedValue) {
            return
        }
        setActiveIndex(0)
        setDropdownOpen(suggestions.length > 0)
    }, [suggestions, typedValue])

    if (readOnly) {
        return (
            <FormField
                className={className}
                fieldClassName={fieldClassName}
                labelClassName={labelClassName}
                label={label}
                name={name}
                autoComplete={autoComplete}
                labelTooltip={labelTooltip}
                inputTooltip={inputTooltip}
                description={description}
                placeholder={placeholder}
                mandatory={mandatory}
                readOnly={readOnly}
                showErrors={showErrors}
                height={height}
                data-testid={testId}
            />
        )
    }

    function getHeight(): string {
        switch (height) {
            case 'full':
                return 'h-full'
            case 'small':
                return 'h-8'
            default:
                return 'h-10'
        }
    }

    function inputClassName(): string {
        let classes =
            'h-full rounded-lg flex-1 min-w-0 block w-full px-2 py-1 border text-sm border-secondary text-foreground-tertiary shadow-xs focus:outline-none focus:ring-0 focus:border-primary-500'
        if (!isWidgetField) {
            classes += ' placeholder:text-foreground-muted'
        }
        if (showFieldError) {
            classes += ' border-red-500'
        }
        return classes
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>): void {
        void helpers.setValue(event.target.value)
        setTypedValue(event.target.value)
        if (!event.target.value.trim()) {
            setDropdownOpen(false)
        }
    }

    function selectStreet(item: SuggestionItem): void {
        void helpers.setValue(item.label)
        const suggestion = findSuggestion(suggestions, item, zipFilter)
        if (suggestion && !zipFilter) {
            if (zipFieldName && !siblingZip) {
                void setFieldValue(zipFieldName, suggestion.postalCode)
            }
            if (cityFieldName && !cityFilter) {
                void setFieldValue(cityFieldName, suggestion.locality)
            }
        }
        setTypedValue('')
        setDropdownOpen(false)
    }

    return (
        <div className={cn('relative', className)}>
            {label ? (
                <div className="flex w-full items-center justify-between gap-2">
                    <Label htmlFor={name} mandatory={mandatory} className={labelClassName}>
                        {label}
                    </Label>
                    {labelTooltip ? (
                        <QuestionMarkCircleIcon
                            className="h-5 w-5 shrink-0 text-foreground-muted inline"
                            data-tooltip-id={name}
                            data-tooltip-content={labelTooltip}
                        />
                    ) : null}
                </div>
            ) : null}
            <div
                className={cn('flex rounded-md shadow-xs relative', getHeight())}
                {...(inputTooltipId
                    ? {
                          'data-tooltip-id': inputTooltipId,
                          'data-tooltip-content': inputTooltip
                      }
                    : {})}
            >
                <input
                    data-testid={testId ?? `field-${name}`}
                    id={name}
                    name={name}
                    type="text"
                    autoComplete={autoComplete}
                    className={cn(inputClassName(), fieldClassName)}
                    required={mandatory}
                    placeholder={placeholder}
                    value={field.value ?? ''}
                    onChange={handleChange}
                    onKeyDown={(event) =>
                        handleSuggestionKeyDown(event, {
                            open: dropdownOpen,
                            items,
                            activeIndex,
                            setActiveIndex,
                            onSelect: selectStreet,
                            onClose: () => setDropdownOpen(false)
                        })
                    }
                    onBlur={(event) => {
                        setDropdownOpen(false)
                        field.onBlur(event)
                    }}
                    role="combobox"
                    aria-expanded={dropdownOpen}
                    aria-controls={`${name}-suggestions`}
                />
                {dropdownOpen ? (
                    <SuggestionDropdown
                        id={`${name}-suggestions`}
                        items={items}
                        activeIndex={activeIndex}
                        onSelect={selectStreet}
                        onActiveIndexChange={setActiveIndex}
                        isWidget={isWidgetField}
                    />
                ) : null}
            </div>
            {labelTooltip ? <Tooltip id={name} /> : null}
            {inputTooltipId ? (
                <Tooltip
                    id={inputTooltipId}
                    positionStrategy="fixed"
                    opacity={1}
                    className="z-9999! max-w-xs! rounded! bg-primary-800! p-3! text-xs! font-medium! leading-snug! text-white!"
                />
            ) : null}
            {showErrors ? <ErrorMessage name={name} /> : null}
            <Description description={description} />
        </div>
    )
}

function findSuggestion(
    suggestions: StreetSuggestion[],
    item: SuggestionItem,
    zipFilter: string | undefined
): StreetSuggestion | undefined {
    return suggestions.find((street) => {
        const id = !zipFilter ? `${street.name}-${street.postalCode}-${street.locality}` : street.name
        return id === item.id
    })
}
